import { Chapter, Standard } from "../types";

export interface CourseData {
  courseId: string;
  courseTitle: string;
  courseDescription: string;
  platformType: "90-hours" | "45-hours" | "30-hours";
}

export interface CartridgeLesson {
  id: string;
  title: string;
  type: "html" | "external_url" | "discussion" | "assignment" | "lti_link";
  content?: string;
  description?: string;
  url?: string;
  ltiUrl?: string;
  ltiCustomProperties?: Array<{
    name: string;
    value: string;
  }>;
  assets?: Array<{
    url: string;
    fileName: string;
  }>;
}

export interface CartridgeChapter {
  id: string;
  title: string;
  lessons: CartridgeLesson[];
}

export interface CartridgeStandard {
  id: string;
  title: string;
  description: string;
  chapters: CartridgeChapter[];
}

export interface CartridgeExportData {
  courseId: string;
  courseTitle: string;
  courseDescription: string;
  standards: CartridgeStandard[];
}

function getStandardIndex(standards: Standard[], standardId: string): number {
  return standards.findIndex((s) => s.id === standardId);
}

function getChapterIndex(chapters: Chapter[], chapterId: string): number {
  return chapters.findIndex((c) => c.id === chapterId);
}

const ltiUrl = `${import.meta.env.VITE_API_BASE_URL}/lti`;

/**
 * Transforms the internal standards format to Common Cartridge format
 */
export function transformToCartridgeFormat(
  standards: Standard[],
  courseData: CourseData,
): CartridgeExportData {
  // find the index of the standard in the standards array
  return {
    courseId: courseData.courseId,
    courseTitle: courseData.courseTitle,
    courseDescription: courseData.courseDescription,
    standards: standards.map((standard) => ({
      id: standard.id,
      title: standard.title,
      description: standard.overview,
      chapters: standard.chapters.map((chapter) => ({
        id: chapter.id,
        title: chapter.title,
        lessons: transformChapterToLessons(
          chapter,
          courseData.platformType,
          getStandardIndex(standards, standard.id),
          getChapterIndex(standard.chapters, chapter.id),
        ),
      })),
    })),
  };
}

/**
 * Transforms a chapter into lessons for Common Cartridge
 */
function transformChapterToLessons(
  chapter: any,
  platformType: "90-hours" | "45-hours" | "30-hours",
  standardIndex: number,
  chapterIndex: number,
): CartridgeLesson[] {
  const lessons: CartridgeLesson[] = [];

  if (!chapter) {
    console.warn("transformChapterToLessons: chapter is null or undefined");
    return lessons;
  }

  // Handle 30-hours platform - use content and reflections only
  if (platformType === "30-hours") {
    return get30HoursChapterLessons(chapter, standardIndex, chapterIndex);
  }

  // Handle 45-hours platform - only include dayone content
  if (platformType === "45-hours") {
    return get45HoursChapterLessons(chapter, standardIndex, chapterIndex);
  }

  // Handle 90-hours platform - include everything (current implementation)
  // Add main chapter content as a lesson
  if (chapter.content) {
    lessons.push({
      id: `${chapter.id}-content`,
      title: `${chapter.title} - Main Content`,
      type: "html",
      content: transformContentToHTML(chapter),
      assets: extractAssetsFromContent(chapter.content),
    });
  }

  return get90HoursChapterLessons(chapter, standardIndex, chapterIndex);
}

/**
 * Transforms chapter content to HTML format
 */
function transformContentToHTML(chapter: any): string {
  let html = `<h1>${chapter.title}</h1>`;

  if (chapter.description) {
    html += `<p class="chapter-description">${chapter.description}</p>`;
  }

  if (chapter.content) {
    html += `<div class="chapter-content">${chapter.content}</div>`;
  }

  if (chapter.learningObjectives && chapter.learningObjectives.length > 0) {
    html += `<h2>Learning Objectives</h2><ul>`;
    chapter.learningObjectives.forEach((objective: string) => {
      html += `<li>${objective}</li>`;
    });
    html += `</ul>`;
  }

  if (chapter.readingTime) {
    html += `<p><strong>Estimated reading time:</strong> ${chapter.readingTime}</p>`;
  }

  return html;
}

/**
 * Transforms 30-hours chapter content to HTML format
 */
function transform30HoursContentToHTML(chapter: any): string {
  let html = `<h1>${chapter.title}</h1>`;

  if (chapter.description) {
    html += `<p class="chapter-description">${chapter.description}</p>`;
  }

  if (chapter.content) {
    html += `<div class="chapter-content">${chapter.content}</div>`;
  }

  if (chapter.learningObjectives && chapter.learningObjectives.length > 0) {
    html += `<h2>Learning Objectives</h2><ul>`;
    chapter.learningObjectives.forEach((objective: string) => {
      html += `<li>${objective}</li>`;
    });
    html += `</ul>`;
  }

  if (chapter.readingTime) {
    html += `<p><strong>Estimated reading time:</strong> ${chapter.readingTime}</p>`;
  }

  // Add skill builder details if it exists
  if (chapter.skillBuilder && chapter.skillBuilder.title) {
    html += `<h2>Skill Builder</h2>`;
    html += `<h3>${chapter.skillBuilder.title}</h3>`;
    html += `<p><strong>Description:</strong> ${
      chapter.skillBuilder.description || "No description available"
    }</p>`;
    html += `<p><strong>Activity:</strong> ${
      chapter.skillBuilder.activity || "No activity specified"
    }</p>`;

    if (
      chapter.skillBuilder.sections &&
      chapter.skillBuilder.sections.length > 0
    ) {
      chapter.skillBuilder.sections.forEach(
        (section: any, sectionIndex: number) => {
          if (section && section.title) {
            html += `<h3>${section.title}</h3>`;
            if (section.categories && typeof section.categories === "object") {
              Object.entries(section.categories).forEach(
                ([catKey, cat]: [string, any]) => {
                  try {
                    if (
                      cat &&
                      cat.label &&
                      cat.options &&
                      Array.isArray(cat.options)
                    ) {
                      html += `<strong>${cat.label}:</strong> <ul>`;
                      cat.options.forEach((opt: string) => {
                        html += `<li>${opt}</li>`;
                      });
                      html += `</ul>`;
                    } else {
                      console.warn(
                        `Skipping category in section ${sectionIndex}:`,
                        { catKey, cat },
                      );
                    }
                  } catch (error) {
                    console.error(
                      `Error processing category ${catKey} in section ${sectionIndex}:`,
                      error,
                      { cat },
                    );
                  }
                },
              );
            }
          }
        },
      );
    }
  }

  // Add images section if available
  if (
    chapter.imagesSection &&
    chapter.imagesSection.images &&
    chapter.imagesSection.images.length > 0
  ) {
    html += `<h2>${chapter.imagesSection.title || "Images"}</h2>`;
    if (chapter.imagesSection.description) {
      html += `<p>${chapter.imagesSection.description}</p>`;
    }
    html += `<div class="images-section">`;
    chapter.imagesSection.images.forEach((imageUrl: string) => {
      html += `<img src="${imageUrl}" alt="Chapter image" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
    });
    html += `</div>`;
  }

  return html;
}

/**
 * Transforms day content to HTML format
 */
function transformDayContentToHTML(dayContent: any): string {
  if (!dayContent) {
    console.warn("transformDayContentToHTML: dayContent is null or undefined");
    return "<h2>Day Content</h2><p>Content not available</p>";
  }

  let html = `<h2>${dayContent.description || "Day Content"}</h2>`;

  if (
    dayContent.learningObjectives &&
    dayContent.learningObjectives.length > 0
  ) {
    html += `<h3>Learning Objectives</h3><ul>`;
    dayContent.learningObjectives.forEach((objective: string) => {
      html += `<li>${objective}</li>`;
    });
    html += `</ul>`;
  }

  if (dayContent.introduction) {
    if (typeof dayContent.introduction === "string") {
      html += `<div class="introduction">${dayContent.introduction}</div>`;
    } else if (dayContent.introduction.content) {
      html += `<div class="introduction">${dayContent.introduction.content}</div>`;
    }
  }

  if (dayContent.keyConcepts) {
    html += `<h3>Key Concepts</h3><div class="key-concepts">${dayContent.keyConcepts}</div>`;
  }

  if (dayContent.deeperExploration) {
    html += `<h3>Deeper Exploration</h3><div class="deeper-exploration">${dayContent.deeperExploration}</div>`;
  }

  if (dayContent.realWorldExamples) {
    html += `<h3>Real World Examples</h3><div class="real-world-examples">${dayContent.realWorldExamples}</div>`;
  }

  if (dayContent.summary) {
    html += `<h3>Summary</h3><div class="summary">${dayContent.summary}</div>`;
  }

  if (dayContent.activities && dayContent.activities.length > 0) {
    html += `<h3>Activities</h3><ul>`;
    dayContent.activities.forEach((activity: any) => {
      if (activity && activity.title) {
        html += `<li><strong>${activity.title}</strong> - ${
          activity.time || "Duration not specified"
        }</li>`;
      }
    });
    html += `</ul>`;
  }

  // Reflections are handled separately as discussion topics, not included in day content

  if (dayContent.skillBuilder && dayContent.skillBuilder.title) {
    html += `<h2>Skill Builder</h2>`;
    html += `<h3>${dayContent.skillBuilder.title}</h3>`;
    html += `<p>${
      dayContent.skillBuilder.description || "No description available"
    }</p>`;
    html += `<p><strong>Activity:</strong> ${
      dayContent.skillBuilder.activity || "No activity specified"
    }</p>`;

    if (
      dayContent.skillBuilder.sections &&
      dayContent.skillBuilder.sections.length > 0
    ) {
      dayContent.skillBuilder.sections.forEach(
        (section: any, sectionIndex: number) => {
          if (section && section.title) {
            html += `<h3>${section.title}</h3>`;
            if (section.categories && typeof section.categories === "object") {
              Object.entries(section.categories).forEach(
                ([catKey, cat]: [string, any]) => {
                  try {
                    if (
                      cat &&
                      cat.label &&
                      cat.options &&
                      Array.isArray(cat.options)
                    ) {
                      html += `<strong>${cat.label}:</strong> <ul>`;
                      cat.options.forEach((opt: string) => {
                        html += `<li>${opt}</li>`;
                      });
                      html += `</ul>`;
                    } else {
                      console.warn(
                        `Skipping category in section ${sectionIndex}:`,
                        { catKey, cat },
                      );
                    }
                  } catch (error) {
                    console.error(
                      `Error processing category ${catKey} in section ${sectionIndex}:`,
                      error,
                      { cat },
                    );
                  }
                },
              );
            }
          }
        },
      );
    }
  }

  if (dayContent.portfolio && dayContent.portfolio.description) {
    html += `<h2>Portfolio</h2>`;
    html += `<p>${dayContent.portfolio.description}</p>`;
    if (dayContent.portfolio.input) {
      if (Array.isArray(dayContent.portfolio.input)) {
        dayContent.portfolio.input.forEach((input: any) => {
          if (input && input.label) {
            html += `<div><strong>${input.label}</strong> (placeholder: ${
              input.placeholder || "No placeholder"
            })</div>`;
          }
        });
      } else if (dayContent.portfolio.input.label) {
        html += `<div><strong>${
          dayContent.portfolio.input.label
        }</strong> (placeholder: ${
          dayContent.portfolio.input.placeholder || "No placeholder"
        })</div>`;
      }
    }
    if (dayContent.portfolio.content) {
      html += `<div>${dayContent.portfolio.content}</div>`;
    }
  }

  return html;
}

/**
 * Extracts assets (images, files) from content
 */
function extractAssetsFromContent(
  content: string,
): Array<{ url: string; fileName: string }> {
  const assets: Array<{ url: string; fileName: string }> = [];

  // Extract image URLs from content
  const imageRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  let match;

  while ((match = imageRegex.exec(content)) !== null) {
    const imageUrl = match[1];
    if (imageUrl && !imageUrl.startsWith("data:")) {
      const fileName = `images/${imageUrl.split("/").pop()}`;
      assets.push({
        url: imageUrl,
        fileName,
      });
    }
  }

  return assets;
}

/**
 * Extracts assets from day content
 */
function extractAssetsFromDayContent(
  dayContent: any,
): Array<{ url: string; fileName: string }> {
  const assets: Array<{ url: string; fileName: string }> = [];

  // Check for podcast audio
  if (dayContent.introduction?.podcast?.audioUrl) {
    const audioUrl = dayContent.introduction.podcast.audioUrl;
    const fileName = `audio/${audioUrl.split("/").pop()}`;
    assets.push({
      url: audioUrl,
      fileName,
    });
  }

  // Extract assets from text content
  const contentFields = [
    "introduction",
    "keyConcepts",
    "deeperExploration",
    "realWorldExamples",
    "summary",
  ];

  contentFields.forEach((field) => {
    let content = "";
    if (typeof dayContent[field] === "string") {
      content = dayContent[field];
    } else if (dayContent[field]?.content) {
      content = dayContent[field].content;
    }

    if (content) {
      assets.push(...extractAssetsFromContent(content));
    }
  });

  return assets;
}

/**
 * Validates the export data
 */
export function validateCartridgeData(data: CartridgeExportData): string[] {
  const errors: string[] = [];

  if (!data.courseId.trim()) {
    errors.push("Course ID is required");
  }

  if (!data.courseTitle.trim()) {
    errors.push("Course title is required");
  }

  if (!data.courseDescription.trim()) {
    errors.push("Course description is required");
  }

  if (!data.standards || data.standards.length === 0) {
    errors.push("At least one standard must be selected");
  }

  data.standards.forEach((standard, standardIndex) => {
    if (!standard.id.trim()) {
      errors.push(`Standard ${standardIndex + 1}: ID is required`);
    }

    if (!standard.title.trim()) {
      errors.push(`Standard ${standardIndex + 1}: Title is required`);
    }

    if (!standard.chapters || standard.chapters.length === 0) {
      errors.push(
        `Standard "${standard.title}": At least one chapter must be selected`,
      );
    }

    standard.chapters.forEach((chapter, chapterIndex) => {
      if (!chapter.id.trim()) {
        errors.push(
          `Chapter ${chapterIndex + 1} in Standard "${
            standard.title
          }": ID is required`,
        );
      }

      if (!chapter.title.trim()) {
        errors.push(
          `Chapter ${chapterIndex + 1} in Standard "${
            standard.title
          }": Title is required`,
        );
      }

      if (!chapter.lessons || chapter.lessons.length === 0) {
        errors.push(
          `Chapter "${chapter.title}": At least one lesson must be available`,
        );
      }
    });
  });

  return errors;
}

// TODO: Need to be implemented
function get30HoursChapterLessons(
  chapter: any,
  standardIndex: number,
  chapterIndex: number,
): CartridgeLesson[] {
  const lessons: CartridgeLesson[] = [];

  lessons.push({
    id: `${chapter.id}-content`,
    title: `${chapter.title} - Main Content`,
    type: "html",
    content: transform30HoursContentToHTML(chapter),
    assets: [
      ...extractAssetsFromContent(chapter.content),
      ...(chapter.imagesSection?.images?.map((imageUrl: string) => ({
        url: imageUrl,
        fileName: `images/${imageUrl.split("/").pop()}`,
      })) || []),
    ],
  });

  const reflectionsId = `standard${standardIndex + 1}-chapter${
    chapterIndex + 1
  }-30h`;
  const reflectionsTitle = `Reflections`;
  lessons.push({
    id: reflectionsId,
    title: reflectionsTitle,
    type: "lti_link",
    ltiUrl,
    ltiCustomProperties: [
      { name: "name", value: reflectionsTitle },
      { name: "slug", value: reflectionsId },
      {
        name: "value",
        value: `/lti/resources/${reflectionsId}`,
      },
      { name: "author", value: "Project Explore" },
    ],
  });

  // Add skill builder as LTI link if it exists
  if (chapter.skillBuilder) {
    const skillBuilderId = chapter.skillBuilder.type;
    const skillBuilderTitle = chapter.skillBuilder.title;
    lessons.push({
      id: skillBuilderId,
      title: `Skill Builder: ${skillBuilderTitle}`,
      type: "lti_link",
      ltiUrl,
      ltiCustomProperties: [
        { name: "name", value: skillBuilderTitle },
        { name: "slug", value: skillBuilderId },
        {
          name: "value",
          value: `/lti/resources/${skillBuilderId}`,
        },
        { name: "author", value: "Project Explore" },
      ],
    });
  }

  return lessons;
}

function get45HoursChapterLessons(
  chapter: any,
  standardIndex: number,
  chapterIndex: number,
): CartridgeLesson[] {
  const lessons: CartridgeLesson[] = [];

  if (chapter.dayone) {
    try {
      lessons.push({
        id: `${chapter.id}-dayone`,
        title: `${chapter.title}`,
        type: "html",
        content: transformDayContentToHTML(chapter.dayone),
        assets: extractAssetsFromDayContent(chapter.dayone),
      });
    } catch (error) {
      console.error(
        "Error processing dayone content for chapter:",
        chapter.id,
        error,
      );
    }
  }

  const reflectionsId = `standard${standardIndex + 1}-chapter${
    chapterIndex + 1
  }-reflections`;
  const reflectionsTitle = `Reflections`;
  lessons.push({
    id: reflectionsId,
    title: reflectionsTitle,
    type: "lti_link",
    ltiUrl,
    ltiCustomProperties: [
      { name: "name", value: reflectionsTitle },
      { name: "slug", value: reflectionsId },
      {
        name: "value",
        value: `/lti/resources/${reflectionsId}`,
      },
      { name: "author", value: "Project Explore" },
    ],
  });

  // Add skill builder as LTI link if it exists
  if (chapter.dayone?.skillBuilder) {
    const skillBuilderId = chapter.dayone.skillBuilder.type;
    const skillBuilderTitle = chapter.dayone.skillBuilder.title;
    lessons.push({
      id: skillBuilderId,
      title: `Skill Builder: ${skillBuilderTitle}`,
      type: "lti_link",
      ltiUrl,
      ltiCustomProperties: [
        { name: "name", value: skillBuilderTitle },
        { name: "slug", value: skillBuilderId },
        {
          name: "value",
          value: `/lti/resources/${skillBuilderId}`,
        },
        { name: "author", value: "Project Explore" },
      ],
    });
  }

  return lessons;
}

function get90HoursChapterLessons(
  chapter: any,
  standardIndex: number,
  chapterIndex: number,
): CartridgeLesson[] {
  const lessons: CartridgeLesson[] = [];

  // Add day one content if it exists
  if (chapter.dayone) {
    try {
      lessons.push({
        id: `${chapter.id}-dayone`,
        title: `${chapter.title} - Day One`,
        type: "html",
        content: transformDayContentToHTML(chapter.dayone),
        assets: extractAssetsFromDayContent(chapter.dayone),
      });
    } catch (error) {
      console.error(
        "Error processing dayone content for chapter:",
        chapter.id,
        error,
      );
    }
  }

  // Add day two content if it exists
  if (chapter.daytwo) {
    try {
      lessons.push({
        id: `${chapter.id}-daytwo`,
        title: `${chapter.title} - Day Two`,
        type: "html",
        content: transformDayContentToHTML(chapter.daytwo),
        assets: extractAssetsFromDayContent(chapter.daytwo),
      });
    } catch (error) {
      console.error(
        "Error processing daytwo content for chapter:",
        chapter.id,
        error,
      );
    }
  }

  const reflectionsId = `standard${standardIndex + 1}-chapter${
    chapterIndex + 1
  }-reflections`;
  const reflectionsTitle = `Reflections`;
  lessons.push({
    id: reflectionsId,
    title: reflectionsTitle,
    type: "lti_link",
    ltiUrl,
    ltiCustomProperties: [
      { name: "name", value: reflectionsTitle },
      { name: "slug", value: reflectionsId },
      {
        name: "value",
        value: `/lti/resources/${reflectionsId}`,
      },
      { name: "author", value: "Project Explore" },
    ],
  });

  // Add skill builder as LTI link if it exists
  if (chapter.dayone?.skillBuilder) {
    const skillBuilderId = chapter.dayone.skillBuilder.type;
    const skillBuilderTitle = chapter.dayone.skillBuilder.title;
    lessons.push({
      id: skillBuilderId,
      title: `Skill Builder: ${skillBuilderTitle}`,
      type: "lti_link",
      ltiUrl,
      ltiCustomProperties: [
        { name: "name", value: skillBuilderTitle },
        { name: "slug", value: skillBuilderId },
        {
          name: "value",
          value: `/lti/resources/${skillBuilderId}`,
        },
        { name: "author", value: "Project Explore" },
      ],
    });
  }

  if (chapter.daytwo && chapter.daytwo.activities) {
    chapter.daytwo.activities.forEach((activity: any) => {
      const activityId = activity.type;
      const activityTitle = activity.title;
      lessons.push({
        id: activityId,
        title: `Activity: ${activityTitle}`,
        type: "lti_link",
        ltiUrl,
        ltiCustomProperties: [
          { name: "name", value: `Activity: ${activityTitle}` },
          { name: "slug", value: activityId },
          { name: "value", value: `/lti/resources/${activityId}` },
          { name: "author", value: "Project Explore" },
        ],
      });
    });
  }

  return lessons;
}
